import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import BuyCreditsModal from "@/components/UpgradeModal";
import InvoicesModal from "@/components/InvoicesModal";
import AutoRechargeModal from "@/components/AutoRechargeModal";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Sun, Moon, Languages, FileText, History, LogOut, Sparkles, User, ShieldCheck, RefreshCw, XCircle, Receipt, Zap } from "lucide-react";

const NavLink = ({ to, icon: Icon, label, active }) => (
  <Link to={to} data-testid={`nav-${label.toLowerCase()}`}
    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
      active ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"
    }`}>
    <Icon className="h-4 w-4" /> {label}
  </Link>
);

export default function Navbar() {
  const { user, logout, refresh } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const [showBuy, setShowBuy] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [showAutoRecharge, setShowAutoRecharge] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState(false);

  const subscribed = user?.sub_status === "active";
  const canceling = user?.sub_cancel_at_period_end;

  const links = [
    { to: "/app", icon: Languages, label: "Testo" },
    { to: "/documents", icon: FileText, label: "Documenti" },
    { to: "/history", icon: History, label: "Cronologia" },
  ];
  if (user?.role === "admin") links.push({ to: "/admin", icon: ShieldCheck, label: "Admin" });

  const cancelSub = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/cancel");
      await refresh();
      toast.success("Abbonamento disdetto. Resterà attivo fino alla fine del periodo pagato.");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
      setConfirmCancel(false);
    }
  };

  const resumeSub = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/resume");
      await refresh();
      toast.success("Abbonamento riattivato.");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 glass bg-background/70 border-b border-border/40">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <Link to={user ? "/app" : "/"} data-testid="logo" className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Freed Words" className="h-8 w-auto" />
          <span className="font-heading text-lg font-semibold tracking-tight">
            Freed<span className="text-brand-pink">Words</span>
          </span>
        </Link>

        {user && (
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => <NavLink key={l.to} {...l} active={loc.pathname === l.to} />)}
          </nav>
        )}

        <div className="flex items-center gap-2">
          <button data-testid="theme-toggle" onClick={toggle}
            className="h-9 w-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          {user ? (
            <>
              <button data-testid="credits-pill" onClick={() => setShowBuy(true)}
                className="hidden sm:flex items-center gap-1.5 rounded-full border border-border bg-card px-3 h-9 text-sm hover:border-brand-pink/50 transition-colors">
                <Sparkles className="h-3.5 w-3.5 text-brand-pink" />
                {user.unlimited
                  ? <span className="font-medium text-brand-pink">Illimitato ∞</span>
                  : <><span className="tabular-nums font-medium">{(user.remaining ?? 0).toLocaleString("it-IT")}</span><span className="text-muted-foreground text-xs">battute</span></>}
              </button>
              {!user.unlimited && (
                <Button data-testid="buy-credits-nav-btn" onClick={() => setShowBuy(true)}
                  className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white gap-1.5 hidden sm:flex">
                  <Sparkles className="h-4 w-4" /> {subscribed ? "Acquista battute" : "Abbonati"}
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu"
                    className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center text-sm font-semibold hover:ring-2 hover:ring-brand-blue/50 transition-all">
                    {user.name?.[0]?.toUpperCase() || <User className="h-4 w-4" />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    <span className={`inline-flex items-center gap-1 mt-1.5 text-[10px] font-mono-label uppercase tracking-wider px-2 py-0.5 rounded-full ${subscribed ? "bg-brand-pink/15 text-brand-pink" : "bg-secondary text-muted-foreground"}`}>
                      {subscribed ? <><RefreshCw className="h-3 w-3" /> Abbonato{canceling ? " · in disdetta" : ""}</> : "Nessun abbonamento"}
                    </span>
                  </div>
                  <DropdownMenuSeparator />
                  <div className="md:hidden">
                    {links.map((l) => (
                      <DropdownMenuItem key={l.to} onClick={() => nav(l.to)} data-testid={`menu-${l.label.toLowerCase()}`}>
                        <l.icon className="h-4 w-4 mr-2" /> {l.label}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </div>
                  <DropdownMenuItem onClick={() => setShowBuy(true)} data-testid="menu-buy">
                    <Sparkles className="h-4 w-4 mr-2 text-brand-pink" /> {subscribed ? "Acquista battute" : "Abbonati"}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowInvoices(true)} data-testid="menu-invoices">
                    <Receipt className="h-4 w-4 mr-2 text-brand-blue" /> Le mie fatture
                  </DropdownMenuItem>
                  {!user.unlimited && (
                    <DropdownMenuItem onClick={() => setShowAutoRecharge(true)} data-testid="menu-auto-recharge">
                      <Zap className="h-4 w-4 mr-2 text-amber-500" /> Auto-ricarica
                    </DropdownMenuItem>
                  )}
                  {subscribed && !canceling && (
                    <DropdownMenuItem data-testid="menu-cancel-sub" onClick={(e) => { e.preventDefault(); setConfirmCancel(true); }} className="text-destructive focus:text-destructive">
                      <XCircle className="h-4 w-4 mr-2" /> Disdici abbonamento
                    </DropdownMenuItem>
                  )}
                  {subscribed && canceling && (
                    <DropdownMenuItem data-testid="menu-resume-sub" onClick={resumeSub} disabled={busy}>
                      <RefreshCw className="h-4 w-4 mr-2 text-brand-blue" /> Riattiva abbonamento
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}>
                    <LogOut className="h-4 w-4 mr-2" /> Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" data-testid="login-nav" onClick={() => nav("/login")} className="rounded-full">Accedi</Button>
              <Button data-testid="register-nav" onClick={() => nav("/register")} className="rounded-full bg-brand-blue hover:bg-brand-blue/90 text-white">Inizia gratis</Button>
            </>
          )}
        </div>
      </div>

      <BuyCreditsModal open={showBuy} onOpenChange={setShowBuy} />
      <InvoicesModal open={showInvoices} onOpenChange={setShowInvoices} />
      <AutoRechargeModal open={showAutoRecharge} onOpenChange={setShowAutoRecharge} />

      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent data-testid="cancel-sub-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Disdire l'abbonamento?</AlertDialogTitle>
            <AlertDialogDescription>
              L'abbonamento resterà attivo fino alla fine del periodo già pagato, poi non verrà rinnovato.
              Le battute extra acquistate restano comunque disponibili.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-sub-abort">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="cancel-sub-confirm" onClick={(e) => { e.preventDefault(); cancelSub(); }} disabled={busy}
              className="bg-destructive hover:bg-destructive/90">
              {busy ? "Attendi..." : "Sì, disdici"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}
